
import { PieceType } from "@/shogi";
import { computed, reactive, watch, defineComponent, PropType } from "vue";
import LayoutBuilder, {
  BoardImageType,
  PieceImageType,
} from "@/components/primitive/BoardLayout";
import { Square, Piece, Color, Move, ImmutablePosition } from "@/shogi";
import { RectSize } from "./Types";
import { secondsToHMMSS } from "@/helpers/time";

type State = {
  pointer: Square | Piece | null;
  reservedMove: Move | null;
};

export default defineComponent({
  name: "BoardView",
  props: {
    pieceImageType: {
      type: String as PropType<PieceImageType>,
      required: true,
    },
    boardImageType: {
      type: String as PropType<BoardImageType>,
      required: true,
    },
    maxSize: {
      type: RectSize,
      required: true,
    },
    position: {
      type: Object as PropType<ImmutablePosition>,
      required: true,
    },
    lastMove: {
      type: Move,
      required: false,
      default: null,
    },
    flip: {
      type: Boolean,
      required: false,
    },
    allowEdit: {
      type: Boolean,
      required: false,
    },
    allowMove: {
      type: Boolean,
      required: false,
    },
    blackPlayerName: {
      type: String,
      required: false,
      default: "先手",
    },
    whitePlayerName: {
      type: String,
      required: false,
      default: "後手",
    },
    blackPlayerTimeMs: {
      type: Number,
      required: false,
      default: undefined,
    },
    blackPlayerByoyomi: {
      type: Number,
      required: false,
      default: undefined,
    },
    whitePlayerTimeMs: {
      type: Number,
      required: false,
      default: undefined,
    },
    whitePlayerByoyomi: {
      type: Number,
      required: false,
      default: undefined,
    },
  },
  emits: ["move", "edit"],
  setup(props, context) {
    const state = reactive({
      pointer: null,
      reservedMove: null,
    } as State);

    const resetState = () => {
      state.pointer = null;
      state.reservedMove = null;
    };

    watch(
      [() => props.position, () => props.allowEdit, () => props.allowMove],
      () => {
        resetState();
      }
    );

    const clickFrame = () => {
      resetState();
    };

    const updatePointer = (
      newPointer: Square | Piece,
      empty: boolean,
      color: Color | undefined
    ) => {
      const prevPointer = state.pointer;
      resetState();
      if (
        newPointer instanceof Square &&
        prevPointer instanceof Square &&
        newPointer.equals(prevPointer)
      ) {
        return;
      }
      if (
        newPointer instanceof Piece &&
        prevPointer instanceof Piece &&
        newPointer.equals(prevPointer)
      ) {
        return;
      }
      if (prevPointer) {
        const editFrom = prevPointer;
        const editTo =
          newPointer instanceof Square ? newPointer : newPointer.color;
        if (
          props.allowEdit &&
          props.position.isValidEditing(editFrom, editTo)
        ) {
          context.emit("edit", {
            move: {
              from: prevPointer,
              to: editTo,
            },
          });
          return;
        }
        if (props.allowMove && newPointer instanceof Square) {
          const moveFrom =
            prevPointer instanceof Square ? prevPointer : prevPointer.type;
          const moveTo = newPointer;
          const move = props.position.createMove(moveFrom, moveTo);
          if (!move) {
            return;
          }
          const noProm = props.position.isValidMove(move);
          const prom = props.position.isValidMove(move.withPromote());
          if (noProm && prom) {
            state.reservedMove = move;
            return;
          }
          if (noProm) {
            context.emit("move", move);
            return;
          }
          if (prom) {
            context.emit("move", move.withPromote());
            return;
          }
        }
      }
      if ((!props.allowMove && !props.allowEdit) || empty) {
        return;
      }
      if (!props.allowEdit && color !== props.position.color) {
        return;
      }
      state.pointer = newPointer;
    };

    const clickSquare = (event: Event, file: number, rank: number) => {
      event.stopPropagation();
      event.preventDefault();
      const square = new Square(file, rank);
      const piece = props.position.board.at(square);
      const empty = !piece;
      updatePointer(square, empty, piece?.color);
    };

    const clickHand = (event: Event, color: Color, type: PieceType) => {
      event.stopPropagation();
      event.preventDefault();
      const empty = props.position.hand(color).count(type) === 0;
      updatePointer(new Piece(color, type), empty, color);
    };

    const clickSquareR = (event: Event, file: number, rank: number) => {
      event.stopPropagation();
      event.preventDefault();
      resetState();
      const square = new Square(file, rank);
      if (props.allowEdit && props.position.board.at(square)) {
        context.emit("edit", { rotate: square });
      }
    };

    const clickPromote = (event: Event) => {
      event.stopPropagation();
      event.preventDefault();
      const move = state.reservedMove;
      resetState();
      if (move && props.position.isValidMove(move.withPromote())) {
        context.emit("move", move.withPromote());
      }
    };

    const clickNotPromote = (event: Event) => {
      event.stopPropagation();
      event.preventDefault();
      const move = state.reservedMove;
      resetState();
      if (move && props.position.isValidMove(move)) {
        context.emit("move", move);
      }
    };

    const layoutBuilder = computed(() => {
      const builder = new LayoutBuilder(
        props.pieceImageType,
        props.boardImageType
      );
      builder.preload();
      return builder;
    });

    const layout = computed(() =>
      layoutBuilder.value.build(
        props.maxSize.reduce(new RectSize(20, 20)),
        props.position,
        props.lastMove,
        state.pointer,
        state.reservedMove,
        props.flip
      )
    );

    const formatTime = (timeMs?: number, byoyomi?: number): string => {
      if (timeMs) {
        return secondsToHMMSS(Math.ceil(timeMs / 1e3));
      } else if (byoyomi !== undefined) {
        return "" + byoyomi;
      }
      return "0:00:00";
    };

    const timeSeverity = (timeMs?: number, byoyomi?: number) => {
      if (!timeMs && !byoyomi) {
        return "normal";
      }
      const rem = (timeMs || 0) / 1e3 + (byoyomi || 0);
      if (rem <= 5) {
        return "danger";
      } else if (rem <= 10) {
        return "warning";
      }
      return "normal";
    };

    const blackPlayerTimeText = computed(() => {
      return formatTime(props.blackPlayerTimeMs, props.blackPlayerByoyomi);
    });

    const blackPlayerTimeSeverity = computed(() => {
      return timeSeverity(props.blackPlayerTimeMs, props.blackPlayerByoyomi);
    });

    const whitePlayerTimeText = computed(() => {
      return formatTime(props.whitePlayerTimeMs, props.whitePlayerByoyomi);
    });

    const whitePlayerTimeSeverity = computed(() => {
      return timeSeverity(props.whitePlayerTimeMs, props.whitePlayerByoyomi);
    });

    return {
      clickFrame,
      clickSquare,
      clickHand,
      clickSquareR,
      clickPromote,
      clickNotPromote,
      layout,
      blackPlayerTimeText,
      blackPlayerTimeSeverity,
      whitePlayerTimeText,
      whitePlayerTimeSeverity,
    };
  },
});
